'use client'

import React, { ReactNode } from 'react'
import { AlertCircle } from 'lucide-react'

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
}

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: (error: Error, resetError: () => void) => ReactNode
  componentName?: string
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: undefined }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`[ErrorBoundary] ${this.props.componentName || 'Component'} error:`, error, errorInfo)
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error!, this.resetError)
      }

      return (
        <div className="border border-red-200 bg-red-50 rounded-lg p-4 mt-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-red-600 mt-0.5 flex-shrink-0" size={20} />
            <div className="flex-1">
              <h3 className="font-semibold text-red-900">
                {this.props.componentName ? `${this.props.componentName} Error` : 'Something went wrong'}
              </h3>
              <p className="text-sm text-red-800 mt-1">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
              <button
                onClick={this.resetError}
                className="mt-3 px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// For use with async components, you can also use this hook-based approach:
/**
 * USAGE: Wrap components with error boundaries
 * 
 * import { ErrorBoundary } from '@/components/error-boundary'
 * 
 * <ErrorBoundary componentName="MovieDetail">
 *   <MovieDetail {...props} />
 * </ErrorBoundary>
 * 
 * Or with custom fallback:
 * 
 * <ErrorBoundary
 *   componentName="ShowtimesCollapsible"
 *   fallback={(error, reset) => (
 *     <div className="p-4 bg-red-50 rounded">
 *       <p>{error.message}</p>
 *       <button onClick={reset}>Retry</button>
 *     </div>
 *   )}
 * >
 *   <ShowtimesCollapsible {...props} />
 * </ErrorBoundary>
 */
